import {
  USStateFieldOptions,
  validateContainChars,
  validateEmail,
  validateEmailDoublePeriods,
  validateEmailEndPeriods,
  validateEmailStartPeriods,
  validateMax,
  validatePassword,
  validateRequired
} from "wvus-forms";

import { validatePhone, validatePhoneAreacode } from "../helpers/validationHelpers";

export const validators = {
  name: [validateRequired, validateContainChars, validateMax(32)],
  phone: [validateRequired, validatePhoneAreacode, validatePhone],
  email: [
    validateRequired,
    validateEmail,
    validateEmailStartPeriods,
    validateEmailEndPeriods,
    validateEmailDoublePeriods
  ],
  password: [validateRequired, validatePassword]
};

/**
 * Normalizes a phone number by removing all non-digit characters.
 *
 * @param {string} phone - The phone number to be normalized.
 * @returns {string} The normalized phone number containing only digits.
 */
const normalizePhoneToDigits = (phone) => {
  return phone.replace(/\D/g, "");
};

/**
 * Checks if a given question name corresponds to a multi-choice response.
 *
 * @param {string} name - The name of the question to check.
 * @param {Array<Object>} surveyQuestions - An array of survey question objects.
 * @param {string} surveyQuestions[].name - The name of a survey question.
 * @returns {boolean} - Returns true if the question name does not exist in the survey questions, indicating a multi-choice response.
 */
const isMultiChoiceResponse = (name, surveyQuestions) => {
  const surveyQuestionNames = surveyQuestions.map((q) => q.name);
  return !surveyQuestionNames.includes(name);
};

/**
 * Handles the response for a multi-choice question in a survey.
 *
 * @param {string} name - The name of the response, formatted as "questionId:answer".
 * @param {Array} surveyQuestions - An array of survey questions, where each question is an object with a `questionIdentifier` and `questionText`.
 * @param {Array} responseArray - An array of response objects, where each response object contains a `question` and an `answer` array.
 * @param {string} textInputResponse - The text input response for the "other" option.
 *
 * @returns {void} - Modifies the `responseArray` in place.
 */
const handleMultiChoiceResponse = (name, surveyQuestions, responseArray, textInputResponse) => {
  const [questionId, answer] = name.split(":");
  if (!questionId || !answer) return;

  const questionText = surveyQuestions.find(
    (q) => q.questionIdentifier === questionId
  )?.questionText;

  let answerText = "";
  if (answer !== "other") {
    answerText = answer.replace(/_/g, " ");
  } else {
    answerText = textInputResponse;
  }

  const index = responseArray.findIndex((res) => res.question === questionText);
  const targetResponse = index !== -1 ? responseArray[index] : undefined;

  // if the survey responses already contain the question that goes with this answer, add the answer to the corresponding array
  // otherwise, add a new object to the array with the question and answer
  if (targetResponse && Array.isArray(targetResponse.answer)) {
    responseArray[index] = { ...targetResponse, answer: [...targetResponse.answer, answerText] };
  } else {
    responseArray.push({ question: questionText, answer: [answerText] });
  }
};

/**
 * Extracts user values from the given fields based on the specified user keys.
 *
 * @param {Object} fields - The fields containing user data.
 * @param {Array<string>} userKeys - The keys to extract from the fields, eg: firstName.
 *
 * @returns {Object} An object containing the extracted user values.
 */
const extractUserValues = (fields, userKeys) => {
  return Object.entries(fields).reduce(
    (result, [name, answer]) => {
      if (userKeys.includes(name)) {
        result[name] = answer?.value;
      }
      return result;
    },
    { firstName: "", lastName: "", email: "", city: "", state: "", phoneNumber: "" }
  );
};

/**
 * Retrieves the IDs of selected badges based on the form fields and badges from config.
 *
 * Filters the fields object where the value is a truthy boolean and the key is a badge name from config.
 * Then using that key, which is the badge name, filters the badges array to only use those that were selected in the form.
 * Finally maps that array to get the only the badge ID strings.
 *
 * @param {Object} fields - An object representing form fields where keys are field names and values are field objects.
 * @param {Array} badges - An array of badge objects, each containing a `name` property.
 *
 * @returns {Array} An array of selected badge IDs.
 */
const getSelectedBadgeIds = (fields, badges) => {
  const badgeNames = badges.map((badge) => badge.name);
  const selectedBadges = Object.entries(fields).filter(
    ([name, field]) => field.value === true && badgeNames.includes(name)
  );
  const selectedBadgesNames = selectedBadges.map((sb) => sb[0]);
  return badges
    .filter((badge) => selectedBadgesNames.includes(badge.name))
    .map((badge) => badge.id);
};

/**
 * Extracts survey responses from the given fields and survey questions and returns an array in format required by service.
 *
 * @param {Object} fields - The fields containing survey responses.
 * @param {Array} surveyQuestions - The list of survey questions.
 *
 * @returns {Array} An array of objects containing the question text and corresponding answers (answer must be an array).
 */
const extractSurveyResponses = (fields, surveyQuestions) => {
  return Object.entries(fields).reduce((result, [name, answer]) => {
    if (!answer.value) return result;

    if (isMultiChoiceResponse(name, surveyQuestions)) {
      handleMultiChoiceResponse(name, surveyQuestions, result, answer.value);
    } else {
      const question = surveyQuestions.find((q) => q.name === name)?.questionText;
      result.push({ question, answer: [answer.value] });
    }
    return result;
  }, []);
};

/**
 * Configures form data for submission.
 *
 * @param {Object} data - The raw data to be processed.
 * @param {Object[]} data.fields - The fields from the form.
 * @param {Object[]} data.badges - The badges chosen by the user in the form.
 * @param {string} data.supporterNumber - The supporter number.
 * @param {Object} data.widget - The widget information | needs {widgetId, widgetType}.
 * @param {Object[]} data.userDataFields - The user data fields.
 * @param {Object[]} [data.surveyQuestions=[]] - The survey questions.
 *
 * @returns {Object|null} The configured form data or null if fields are not provided. Form data includes:
 *  - {Object[]} SurveyResponse - The survey responses with question text and answers.
 *  - {string} BadgeIds - The list of badge IDs where the value is true.
 *  - {string} DisplayName - The user's full name.
 *  - {string} EmailAddress - The user's email address.
 *  - {string} City - The user's city.
 *  - {string} State - The user's state.
 *  - {string} Phone - The user's phone number (has a default value as service requires SOME phone number).
 *  - {string} SupporterNumber - The supporter number.
 *  - {string} WidgetId - The widget ID.
 *  - {string} WidgetType - The widget type.
 *  - {string} password - The password value.
 *
 */
export const configureFormData = (data) => {
  const { fields, supporterNumber, widget, surveyQuestions, badges, userDataFields = [] } = data;
  if (!fields) return null;

  const { firstName, lastName, email, city, state, phoneNumber } = extractUserValues(
    fields,
    userDataFields
  );

  const formData = {
    SurveyResponse: surveyQuestions && extractSurveyResponses(fields, surveyQuestions),
    BadgeIds: badges && getSelectedBadgeIds(fields, badges),
    DisplayName: `${firstName} ${lastName}`.trim(),
    EmailAddress: email,
    City: city,
    State: state,
    Phone: normalizePhoneToDigits(phoneNumber || "15555555555"), // TODO: remove default phone number - Services seems to require phone
    SupporterNumber: supporterNumber,
    WidgetId: widget?.id,
    WidgetType: widget?.type,
    password: fields.password?.value
  };

  return formData;
};

/**
 * Extracts and returns the attributes for a form field.
 *
 * @param {Object} field - The field configuration object.
 * @param {string} field.label - The label of the field.
 * @param {string} field.placeholder - The placeholder text for the field.
 * @param {string} field.validationType - The type of validation to apply to the field.
 * @param {string} field.name - The name of the field.
 * @param {boolean} field.required - Whether the field is required.
 * @param {boolean} field.editable - Whether the field is editable.
 * @param {string} field.descriptionText - The description text for the field.
 * @param {string} field.prepopulate - The key to prepopulate the field from user data.
 * @param {Object} userData - The userData data object.
 * @param {Object} formFields - The form fields object containing current values.
 * @param {boolean} prepopulateComplete - Whether prepopulation is complete (user data should only prepopulate one time).
 * @returns {Object} The attributes for the form field.
 *  - {string} return.fieldValue - The value of the field.
 *  - {string} return.fieldClasses - The CSS classes for the field.
 *  - {string} return.inputWrapperClasses - The CSS classes for the input wrapper.
 *  - {string} return.inputClasses - The CSS classes for the input.
 *  - {string} return.fieldTitle - The title of the field.
 *  - {string} return.fieldName - The name of the field.
 *  - {string} return.fieldPlaceholder - The placeholder text for the field.
 *  - {boolean} return.optional - Whether the field is optional.
 *  - {string} return.labelClasses - The CSS classes for the label.
 *  - {Array|undefined} return.validators - The validators for the field.
 *  - {string} return.descriptionText - The description text for the field.
 *  - {Object} return.attributes - Additional attributes for the field.
 *  - {boolean} return.attributes.disabled - Whether the field is disabled.
 */
export const getFieldAttributes = (field, userData, formFields, prepopulateComplete = false) => {
  const {
    label,
    placeholder,
    validationType,
    name,
    required,
    descriptionText,
    prepopulate: prepopulateKey
  } = field;
  const canPrepopulateFromUserData =
    !prepopulateComplete && prepopulateKey && userData && prepopulateKey in userData;

  const fieldValue = canPrepopulateFromUserData
    ? userData[prepopulateKey]
    : formFields[name]?.value;
  const fieldValidators = validationType in validators ? validators[validationType] : undefined;
  const disabled = fieldValue && ( field?.editable === false );

  return {
    fieldValue,
    fieldClasses: descriptionText ? "with-gutter" : "",
    inputWrapperClasses: "",
    inputClasses: "",
    fieldTitle: label,
    fieldName: name,
    fieldPlaceholder: placeholder,
    optional: !required,
    labelClasses: required && "is-required",
    validators: fieldValidators,
    descriptionText: descriptionText,
    attributes: { disabled },
    fieldType: field.fieldType
  };
};

/**
 * Retrieves the items for a given form field based on its type (such as a select field's dropdown items).
 *
 * note: one special case is the 'USStateFieldOptions' which is a predefined list of US states. WVUS-Forms provides this list
 * and others so those can be added in the configuration and then handled here in this function to get the right items from
 * WVUS-Forms.
 *
 * @param {Object} field - The form field object.
 * @param {string} field.type - The type of the form field (e.g., 'select', 'multi-select-checkboxes', 'radio').
 * @param {Array|string} [field.items] - The items associated with the form field. Can be an array or a string identifier.
 * @returns {Array} - The items for the form field. Returns an empty array if no items are found.
 */
export const getFieldItems = (field) => {
  switch (field.type) {
    case "select":
      return field.items === "USStateFieldOptions" ? USStateFieldOptions : field.items || [];
    case "multi-select-checkboxes":
    case "radio":
      return field.items || [];
    default:
      return [];
  }
};

export const getUserDataFields = (welcomeConfig) =>
  (welcomeConfig?.fields || []).map((field) => field.name);
